import { Box, Button, Image, ItemInfoButton, Text } from '@bonanza-social/moonlight';
import { useTranslation } from '@bonanza-social/i18n';
import { memo, useCallback } from 'react';
import CashbackTimer from '../CashbackTimer/CashbackTimer';
import { useNavigation } from '@bonanza-social/navigation';
import { CashbackCardProps } from './CashbackCard.types';
import { useDispatch } from '@bonanza-social/toolkit/state';
import { fetchCashback } from '../../state/cashback/cashback.slice';
import { getCashbackStatus } from './CashbackCard.utils';
import { formatAmount, isAfterNow } from '@bonanza-social/toolkit/utils';

function CashbackCard({ cashbackPromo, ...boxProps }: CashbackCardProps) {
  const [t] = useTranslation('cashback');
  const navigation = useNavigation();
  const status = getCashbackStatus(cashbackPromo);
  const dispatch = useDispatch();

  const onInfo = useCallback(() => {
    navigation.navigate('PromoDetails', { id: cashbackPromo.id });
  }, [navigation, cashbackPromo]);

  const onCta = useCallback(() => {
    navigation.navigate('MainTabNavigator', { screen: 'Games' });
  }, [navigation]);

  const refetchCashback = useCallback(
    () => dispatch(fetchCashback()),
    [dispatch],
  );

  return (
    <Box
      {...boxProps}
      padding="space-025"
      gap="space-400"
      testID={`cashback-card-${cashbackPromo.id}`}>
      <Box
        padding="space-400"
        borderRadius="radius-200"
        gap="space-400"
        borderColor="borderWashed"
        borderWidth={
          status.state === 'Active' ? 'border-width-025' : 'border-width-0'
        }
        backgroundColor={
          status.state === 'Active'
            ? 'primaryBackground'
            : 'secondaryBackground'
        }
      >
        <Box flexDirection="row" justifyContent="space-between">
          <CashbackTimer
            promotionEndDate={cashbackPromo.endDate}
            onCountDownComplete={refetchCashback}
            isActive={isAfterNow(cashbackPromo.endDate)}
          />
          {status.state === 'Active' ? (
            <ItemInfoButton onInfo={onInfo} />
          ) : null}
        </Box>
        <Box gap="space-400" flexDirection="row">
          <Image
            name={`cashback.bank${status.state}${status.progress}`}
            width={90}
            height={90}
          />
          <Box gap="space-200" flex={1}>
            <Box gap="space-100">
              <Text variant="body-l-semi" color="darkSurfacePrimary">
                {formatAmount(cashbackPromo.rewards[0].amount)}
              </Text>
              <Text
                variant="caption"
                color="darkSurfacePrimary"
                locNS="cashback"
                locKey={`cashbackMessage_${status.state}_${status.progress}`}
              />
            </Box>
            <Button
              locNS="cashback"
              locKey={`cashbackCta_${status.state}`}
              onPress={onCta}
            />
          </Box>
        </Box>
      </Box>

      <Text variant="caption" color="darkSurfaceSecondary">
        {t(`cashbackCaption_${status.state}_${status.progress}`, {
          amount: formatAmount(cashbackPromo.rewards[0].maxAmount),
        })}
      </Text>
    </Box>
  );
}

export default memo(CashbackCard);
